import { ClapText } from '../motion';
import { RH_MEDIA } from '../../lib/rhapsodyMedia';

/* Real still standing in for the tracked shot — THE RED WALK's red glow reads
   as the "portal" whose emissive state must persist across the cut. Both frames
   draw the SAME source at different crops, i.e. coverage of one set, so the
   continuity claim is believable: same world, two shots, one tracked entity. */
const PROOF_STILL_12 = RH_MEDIA.oneLiner.portalShot12;
const PROOF_STILL_15 = RH_MEDIA.oneLiner.portalShot15;

export function RhapsodyOneLiner() {
  return (
    <section className="rh-section low rh-oneliner-section" id="one-liner">
      {/* Ambient finale backdrop — a soft ember bloom over a faint blueprint grid. */}
      <div className="rh-oneliner-bg" aria-hidden="true">
        <span className="rh-oneliner-grid" />
        <span className="rh-oneliner-glow" />
      </div>

      <div className="rh-oneliner">
        <p className="rh-kicker reveal">In one line</p>
        <ClapText
          text="The [[system of record]] and [[correctness layer]] for AI filmmaking."
          as="h2"
        />
        <p className="rh-body reveal d2" style={{ marginInline: 'auto' }}>
          Rhapsody reasons about story state to plan coherent shots, then verifies every
          shot against causality and craft before it ships.
        </p>

        {/* The "system of record" made literal: a signed continuity report.
            Rhapsody locks onto an entity, carries it across a cut, and stamps
            the verdict. This echoes the Problem section's filmstrip — there
            continuity broke, here it holds and is proven. */}
        <figure className="rh-report reveal d3">
          <figcaption className="rh-report-head">
            <span className="rh-report-id">
              <span className="rh-report-live" aria-hidden="true" />
              Continuity report
              <b>#12&nbsp;→&nbsp;#15</b>
            </span>
            <span className="rh-report-stamp">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3.5 8.4 6.4 11.2 12.5 4.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Verified
            </span>
          </figcaption>

          <div className="rh-report-frames" aria-hidden="true">
            <span className="rh-rframe" data-shot="12">
              <span
                className="rh-rframe-img"
                style={{ backgroundImage: `url(${PROOF_STILL_12})`, backgroundPosition: '46% 40%', backgroundSize: '150%' }}
              />
              <span className="rh-rframe-scan" />
              {/* <span className="rh-rframe-reticle"><b>portal</b></span> */}
              <span className="rh-rframe-slate">SHOT 12</span>
              <span className="rh-rframe-evt">portal opens</span>
            </span>

            <span className="rh-report-link">
              <svg width="60" height="12" viewBox="0 0 60 12" fill="none" aria-hidden="true">
                <path className="rh-link-dash" d="M1 6h50" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="1 5" />
                <path d="M48 1.5 54.5 6 48 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <b>holds</b>
            </span>

            <span className="rh-rframe" data-shot="15">
              <span
                className="rh-rframe-img"
                style={{ backgroundImage: `url(${PROOF_STILL_15})`, backgroundPosition: '60% 48%', backgroundSize: '172%' }}
              />
              <span className="rh-rframe-scan" />
              {/* <span className="rh-rframe-reticle"><b>portal</b></span> */}
              <span className="rh-rframe-slate">SHOT 15</span>
              <span className="rh-rframe-evt">still glowing</span>
            </span>
          </div>

          <div className="rh-report-log">
            <span className="rh-log-row"><span className="k">entity</span><span className="v">portal</span></span>
            <span className="rh-log-row"><span className="k">state</span><span className="v">emissive</span></span>
            <span className="rh-log-row"><span className="k">causality</span><span className="v ok">consistent</span></span>
          </div>

          <p className="rh-report-note">
            <b>Narrative causality</b> — cause and effect inside your story-world. Open a portal in
            shot 12 and it still glows in shot 15. That’s the claim, and the Verifier proves it, cut by cut.
          </p>
        </figure>
      </div>
    </section>
  );
}
